import isString from 'lodash/isString';
import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import { isBranch } from '../../../store/menu/guestRequestMenuSliceHelper';
import countBy from 'lodash/countBy';
import { buildPlainNodesfromMenu } from '@sonifi/wb-tree/utils/helper';
import { GR_NODE_TYPES, MAX_CHILDREN } from '../../../constants/wbGuestRequestConstants';
import { GROUP_DELIVERY_METHOD } from '@sonifi/wb-group/constants/wbGroupConstants';

const validateTitle = ({ title, group }) =>
  new Promise((resolve, reject) => {
    const max = group.market_type !== 'healthcare' ? 30 : 60;

    if (!title) {
      reject(new Error('Item Display Name: Must be provided'));
    } else if (!isString(title)) {
      reject(new Error('Item Display Name: Must be a string.'));
    } else if (title.length > max) {
      reject(
        new Error(
          `Item Display Name: Must be fewer than ${max} characters, was ${title.length}`,
        ),
      );
    } else {
      resolve(true);
    }
  });

const validateVendorIssueId = ({
  vendorIssueId,
  productSettings,
  serviceCenterSettings,
}) =>
  new Promise((resolve, reject) => {
    if (productSettings.delivery_method !== GROUP_DELIVERY_METHOD.integrated) {
      resolve(true);
    }

    if (vendorIssueId === undefined || vendorIssueId === null) {
      reject(
        new Error(
          'Issue Name: You must choose a vendor issue from the drop down.',
        ),
      );
    } else if (!serviceCenterSettings.vendor_issue_map[vendorIssueId]) {
      reject(
        new Error(
          'Issue Id: Issue id not in latest import.  Please update with current vendor id.',
        ),
      );
    } else {
      resolve(true);
    }
  });

const validateInstructions = (instructions) =>
  new Promise((resolve, reject) => {
    const max = 60;

    if (instructions === undefined || instructions === null) {
      resolve(true);
    } else if (!isString(instructions)) {
      reject(new Error('Issue Instruction Text: mMust be a string.'));
    } else if (instructions.length > max) {
      reject(
        new Error(
          `Issue Instruction Text: Must be fewer than ${max} characters, was ${instructions.length}`,
        ),
      );
    } else {
      resolve(true);
    }
  });

const validateOptions = (options) =>
  new Promise((resolve, reject) => {
    if (options && options.length > 0) {
      for (let count = 0; count < options.length; count++) {
        delete options[count].detailTemplate;
        delete options[count].showDetail;
        delete options[count].$$hashKey;

        if (!Array.isArray(options[count].options)) {
          options[count].options = [options[count].options];
        }
        if (
          options[count].type !== 'select' &&
          options[count].type !== 'checkbox'
        ) {
          delete options[count].options;
          resolve(true);
        } else if (
          !options[count].options ||
          options[count].options.length < 1
        ) {
          reject(
            new Error(
              `Options: Must have at least 1 option when '${capitalize(
                options[count].type,
              )}' option type is chosen.`,
            ),
          );
        } else {
          resolve(true);
        }
      }
    } else {
      resolve(true);
    }
  });

const validateVendorOptionIds = ({ options, isOrderByOptions }) =>
  new Promise((resolve, reject) => {
    if (isOrderByOptions) {
      const error = [];
      for (let count = 0; count < options.length; ++count) {
        if (
          options[count].type === 'checkbox' &&
          (options[count].vendor_option_id === undefined ||
            options[count].vendor_option_id === null ||
            options[count].vendor_option_id === '')
        ) {
          error.push(count + 1);
        }
      }
      if (isEmpty(error)) {
        resolve(true);
      } else {
        reject(
          new Error(
            `Options: You must select a vendor option id for option(s) ${error.join(
              ',',
            )}.`,
          ),
        );
      }
    } else {
      resolve(true);
    }
  });

export const processSaveItemNode = ({
  group,
  node,
  productSettings,
  serviceCenterSettings,
  isOrderByOptions,
}) => {
  const subject = node.subject;
  if (productSettings.delivery_method === 'email') {
    subject.vendor_issue_id = null;
  }

  return Promise.all([
    validateTitle({ title: subject.title, group }),
    validateVendorIssueId({
      vendorIssueId: subject.vendor_issue_id,
      productSettings,
      serviceCenterSettings,
    }),
    validateInstructions(subject.instructions),
    validateOptions(subject.options),
    validateVendorOptionIds({ options: subject.options, isOrderByOptions }),
  ]).catch((error) => {
    const body = {
      response: {
        data: [error],
      },
    };
    return Promise.reject(body);
  });
};

export const verifyMenuStructure =
  ({ superStrict }) =>
  (_, getState) => {
    const {
      guestRequestMenu: { serviceMenu, productSettings, serviceCenterSettings },
    } = getState();
    const notifications = { errors: [], warnings: [] };

    const strictnessTarget = superStrict
      ? notifications.errors
      : notifications.warnings;
    const nowDate = new Date().toISOString();

    if (
      superStrict &&
      productSettings.delivery_method === 'email' &&
      serviceCenterSettings.email_addresses.length === 0
    ) {
      notifications.errors.push(
        'An email address must be set in Settings before the content can be published.',
      );
    }

    if (superStrict && !serviceMenu.menu.active) {
      notifications.errors.push(
        'Publishing requires the initial category to be active.',
      );
    }

    let hasActiveCategory = false;
    let hasActiveIssue = false;
    const nodesToUpdate = [];

    const plainMenuNodes = buildPlainNodesfromMenu({
      menu: serviceMenu.menu,
      init: true,
    });

    for (const node of plainMenuNodes) {
      // check for non-categories with childrens
      if (
        node.subject &&
        node.children &&
        !isBranch(node) &&
        node.children.length > 0
      ) {
        notifications.errors.push(
          `${node.subject.title}: An error happened and there are nodes assigned to this item`,
        );
      }
      // check for deleted nodes
      if (node.subject.isZombie) {
        notifications.errors.push(
          'An error happened and a node has been deleted. Tip! Move connected nodes as ' +
            'necessary and delete the node.',
        );
      }

      if (node.subject.hasDeletedContent) {
        strictnessTarget.push(
          `${node.subject.title}: References deleted content that should be properly ` +
            'removed before publishing.',
        );
      }

      if (isBranch(node)) {
        const { true: activeCount } = countBy(node.children, 'active');

        if (activeCount > MAX_CHILDREN) {
          notifications.errors.push(
            `${node.subject.title}: Has ${activeCount} active children, but only ${MAX_CHILDREN} are supported.`,
          );
        }

        if (activeCount) {
          if (node.valid_child_types?.includes(GR_NODE_TYPES.serviceIssues)) {
            hasActiveIssue = true;
          } else if (node.valid_child_types?.includes(GR_NODE_TYPES.serviceCategories)) {
            hasActiveCategory = true;
          }
        }
      } else if (!isBranch(node)) {
        const getVendorId = node.subject.vendor_issue_id;
        if (
          productSettings.delivery_method === GROUP_DELIVERY_METHOD.integrated &&
          productSettings.synchronizes !== true
        ) {
          if (getVendorId === null) {
            notifications.errors.push(
              `${node.subject.title}: Missing vendor id assignment`,
            );

            nodesToUpdate.push(node);
          } else if (!serviceCenterSettings.vendor_issue_map[getVendorId]) {
            notifications.errors.push(
              `${node.subject.title}: Issue Id: Issue id not in latest import.  Please update with current vendor id.`,
            );

            nodesToUpdate.push(node);
          }
        } else if (productSettings.delivery_method === 'email') {
          if (getVendorId !== null) {
            notifications.errors.push(
              `${node.subject.title}: Site setup to be email delivery and there is vendor id associated with this node.  Resave to remove.`,
            );
            nodesToUpdate.push(node);
          }
        }
      }
      // check for non-categories with childrens
      if (
        node.subject &&
        node.children &&
        !isBranch(node) &&
        node.children.length > 0
      ) {
        notifications.errors.push(
          `${node.subject.title}: An error happened and there are nodes assigned to this item`,
        );
      }
      // check for deleted nodes
      if (node.subject.isZombie) {
        notifications.errors.push(
          'An error happened and a node has been deleted. Tip! Move connected nodes as ' +
            'necessary and delete the node.',
        );
      }
    }

    if (superStrict && !hasActiveCategory) {
      strictnessTarget.push(
        'Publishing requires at least one category to be active.',
      );
    }

    if (superStrict && !hasActiveIssue) {
      strictnessTarget.push(
        'Publishing requires at least one issue to be active.',
      );
    }

    return {
      validationResults: notifications,
      nodes: nodesToUpdate,
      lastUpdated: nowDate,
    };
  };
